/**
 * Customer Landing Page Attribution Capture
 * 
 * This script captures landing page information and associates it with customer accounts
 * through a post-registration mechanism and order attributes.
 */

(function() {
  // Constants
  const ATTRIBUTION_STORAGE_KEY = 'landingPageAttribution';
  const ATTRIBUTION_PROCESSED_KEY = 'landingPageAttributionProcessed';
  
  // Function to get the current customer ID if logged in
  function getCurrentCustomerId() {
    // Check if we're on a customer account page
    const accountUrlPattern = /\/account/;
    const onAccountPage = accountUrlPattern.test(window.location.pathname);
    
    if (onAccountPage && window.Shopify && window.Shopify.customer) {
      return window.Shopify.customer.id;
    }
    
    return null;
  }
  
  // Function to check if we're on the account page
  function isAccountPage() {
    return window.location.pathname === '/account';
  }
  
  // Function to store landing page attribution data
  function storeLandingPageAttribution(landingPage, timestamp) {
    if (!landingPage) return;
    
    console.log(`Storing landing page attribution: ${landingPage}`);
    
    const attributionData = {
      landingPage: landingPage,
      timestamp: timestamp || new Date().toISOString(),
      processed: false
    };
    
    try {
      // Store in the new format (JSON)
      localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attributionData));
      sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attributionData));
      
      // Also store in old format for backward compatibility
      localStorage.setItem('landingPage', landingPage);
      sessionStorage.setItem('landingPage', landingPage);
      localStorage.setItem('landingPageTimestamp', attributionData.timestamp);
      sessionStorage.setItem('landingPageTimestamp', attributionData.timestamp);
      
      // Verify that storage worked correctly
      console.log(`Storage validation: localStorage[landingPage]=${localStorage.getItem('landingPage')}`);
      console.log(`Storage validation: sessionStorage[landingPage]=${sessionStorage.getItem('landingPage')}`);
      
      // If we detect we're on a page that has the login/register links, modify them
      setTimeout(() => {
        addLandingPageToLoginLinks(landingPage);
      }, 500);
      
      console.log(`Successfully stored landing page attribution: ${landingPage}`);
    } catch (error) {
      console.error('Error storing landing page attribution:', error);
    }
  }
  
  // Function to add landing page param to login/register links
  function addLandingPageToLoginLinks(landingPage) {
    if (!landingPage) return;
    
    // Find all login and register links
    const loginLinks = document.querySelectorAll('a[href*="/account/login"], a[href*="/account/register"]');
    
    loginLinks.forEach(link => {
      // Skip if already modified
      if (link.dataset.landingPageAdded) return;
      
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('landing_page', landingPage);
      link.href = url.toString();
      link.dataset.landingPageAdded = 'true';
      console.log(`Modified link: ${link.href}`);
    });
  }
  
  // Function to get stored landing page attribution
  function getLandingPageAttribution() {
    try {
      // Try the new format first (JSON)
      let attribution = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
      if (attribution) {
        return JSON.parse(attribution);
      }
      
      attribution = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
      if (attribution) {
        return JSON.parse(attribution);
      }
      
      // Fall back to old format
      const landingPage = localStorage.getItem('landingPage') || sessionStorage.getItem('landingPage');
      const timestamp = localStorage.getItem('landingPageTimestamp') || sessionStorage.getItem('landingPageTimestamp');
      
      if (landingPage) {
        return {
          landingPage: landingPage,
          timestamp: timestamp || new Date().toISOString(),
          processed: false
        };
      }
    } catch (error) {
      console.error('Error retrieving landing page attribution:', error);
    }
    
    return null;
  }
  
  // Function to mark attribution as processed
  function markAttributionAsProcessed() {
    try {
      const attribution = getLandingPageAttribution();
      if (attribution) {
        attribution.processed = true;
        localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
        sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
        
        // Also set a separate flag to avoid processing again
        localStorage.setItem(ATTRIBUTION_PROCESSED_KEY, 'true');
        sessionStorage.setItem(ATTRIBUTION_PROCESSED_KEY, 'true');
      }
    } catch (error) {
      console.error('Error marking attribution as processed:', error);
    }
  }
  
  // Function to extract the landing page identifier from URL
  function extractLandingPageFromUrl() {
    // First check for the parameter
    const urlParams = new URLSearchParams(window.location.search);
    const paramLandingPage = urlParams.get('landing_page');
    if (paramLandingPage) {
      console.log(`Found landing page in URL parameter: ${paramLandingPage}`);
      return paramLandingPage;
    }
    
    // Then check the path
    const pathname = window.location.pathname;
    if (pathname.startsWith('/pages/')) {
      const pathLandingPage = pathname.split('/').pop();
      console.log(`Found landing page in URL path: ${pathLandingPage}`);
      return pathLandingPage;
    }
    
    return null;
  }
  
  // Function to extract landing page from domain
  function extractLandingPageFromDomain() {
    const hostname = window.location.hostname.toLowerCase();
    const domainMapping = {
      'sjcsale.com': 'sjc',
      'www.sjcsale.com': 'sjc',
      'teamsaharasale.com': 'sahara',
      'www.teamsaharasale.com': 'sahara',
      'mtgiftshop.com': 'mt',
      'www.mtgiftshop.com': 'mt',
      'forsythsale.com': 'forsyth',
      'www.forsythsale.com': 'forsyth',
      'capefearsale.com': 'cfvh',
      'www.capefearsale.com': 'cfvh',
      'colquittsale.com': 'colquitt',
      'www.colquittsale.com': 'colquitt',
      'mckinneysale.com': 'mckinney',
      'www.mckinneysale.com': 'mckinney',
      'hendricksale.com': 'hendrick',
      'www.hendricksale.com': 'hendrick',
      'henryfordsale.com': 'hfh',
      'www.henryfordsale.com': 'hfh',
      'huhsale.com': 'huh',
      'www.huhsale.com': 'huh',
      'mgmemployeesale.com': 'mgm',
      'www.mgmemployeesale.com': 'mgm',
      'midlandhealthsale.com': 'midland',
      'www.midlandhealthsale.com': 'midland',
      'umichsale.com': 'umich',
      'www.umichsale.com': 'umich',
      'uncsale.com': 'unc',
      'www.uncsale.com': 'unc',
      'volunteersale.com': 'volunteer',
      'www.volunteersale.com': 'volunteer'
    };
    
    const landingPage = domainMapping[hostname];
    if (landingPage) {
      console.log(`Found landing page from domain: ${landingPage}`);
    }
    return landingPage;
  }
  
  // Main function to initialize attribution tracking
  function initAttributionTracking() {
    console.log('Initializing attribution tracking');
    
    // If we're on a landing page, capture the landing page info
    const urlLandingPage = extractLandingPageFromUrl();
    const domainLandingPage = extractLandingPageFromDomain();
    const landingPage = urlLandingPage || domainLandingPage;
    
    if (landingPage) {
      // Store landing page info for later
      storeLandingPageAttribution(landingPage);
      
      // Also hook the Start Shopping buttons
      const shopButtons = document.querySelectorAll(
        '.henry-ford-shop-btn, .hendrick-shop-btn, .tju-shop-btn, ' + 
        '[id^="start-shopping-btn"], ' +
        '.button[href="/"], .button[href="/collections/all"]'
      );
      
      shopButtons.forEach(button => {
        // Skip if already hooked
        if (button.dataset.attributionHooked) return;
        
        button.dataset.attributionHooked = 'true';
        button.addEventListener('click', function() {
          console.log(`Shop button clicked, storing attribution: ${landingPage}`);
          storeLandingPageAttribution(landingPage);
        });
      });
    }
    
    // Check if we should add landing page info to "login to purchase" links
    const loginToPurchaseLinks = document.querySelectorAll('.login-to-purchase-btn');
    const storedAttribution = getLandingPageAttribution();
    
    if (storedAttribution && storedAttribution.landingPage && loginToPurchaseLinks.length > 0) {
      console.log(`Found ${loginToPurchaseLinks.length} login-to-purchase links, adding landing page: ${storedAttribution.landingPage}`);
      
      loginToPurchaseLinks.forEach(link => {
        // Skip if already modified
        if (link.dataset.landingPageAdded) return;
        
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('landing_page', storedAttribution.landingPage);
        link.href = url.toString();
        link.dataset.landingPageAdded = 'true';
        console.log(`Modified login-to-purchase link: ${link.href}`);
      });
    }
    
    // On the account page, mark attribution as processed
    if (isAccountPage()) {
      const customerId = getCurrentCustomerId();
      const attributionData = getLandingPageAttribution();
      const alreadyProcessed = localStorage.getItem(ATTRIBUTION_PROCESSED_KEY) === 'true' || 
                              sessionStorage.getItem(ATTRIBUTION_PROCESSED_KEY) === 'true';
      
      // If we have attribution data that hasn't been processed yet, mark it
      if (customerId && attributionData && !attributionData.processed && !alreadyProcessed) {
        console.log('Processing customer attribution data...');
        markAttributionAsProcessed();
      }
    }
    
    // Always ensure landing page data is added to forms
    addLandingPageToForms();
  }
  
  // Function to add landing page data to forms
  function addLandingPageToForms() {
    const attributionData = getLandingPageAttribution();
    if (!attributionData || !attributionData.landingPage) return;
    
    // Add to cart form
    const cartForm = document.getElementById('cart') || document.querySelector('form[action="/cart"]');
    if (cartForm) {
      let attributeField = document.querySelector('input[name="attributes[Landing_Page]"]');
      if (!attributeField) {
        attributeField = document.createElement('input');
        attributeField.type = 'hidden';
        attributeField.name = 'attributes[Landing_Page]';
        cartForm.appendChild(attributeField);
      }
      attributeField.value = attributionData.landingPage;
      
      // Also add timestamp
      let timestampField = document.querySelector('input[name="attributes[Landing_Timestamp]"]');
      if (!timestampField) {
        timestampField = document.createElement('input');
        timestampField.type = 'hidden';
        timestampField.name = 'attributes[Landing_Timestamp]';
        cartForm.appendChild(timestampField);
      }
      timestampField.value = attributionData.timestamp;
      
      console.log(`Added landing page to cart attributes: ${attributionData.landingPage}`);
    }
  }
  
  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    initAttributionTracking();
    
    // Also observe for dynamic content changes (AJAX cart updates, etc.)
    const observer = new MutationObserver(function(mutations) {
      // Throttle to avoid excessive processing
      if (window.attributionObserverThrottle) return;
      
      window.attributionObserverThrottle = setTimeout(function() {
        initAttributionTracking();
        window.attributionObserverThrottle = null;
      }, 500);
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  });
})();